import React from 'react';
import { InputField } from '../InputField';
import { SignupFormData, FormErrors, ValidationState } from '../../types';

interface BusinessInfoStepProps {
  formData: SignupFormData;
  errors: FormErrors;
  fcaValidation: ValidationState;
  updateField: (field: keyof SignupFormData, value: string | boolean | string[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  onStepComplete?: (stepName: string) => void;
}

export const BusinessInfoStep: React.FC<BusinessInfoStepProps> = ({
  formData,
  errors,
  fcaValidation,
  updateField,
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  const isStepValid = () => {
    return (
      formData.companyName &&
      !errors.companyName
    );
  };

  const handleNext = () => {
    if (isStepValid()) {
      // Track step completion in HubSpot
      if (onStepComplete) {
        onStepComplete('business_info');
      }
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Tell us a bit about your company
        </h1>
        <p className="text-gray-600">
          We've auto filled this from your FCA number. You can change it if needed.
        </p>
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <span className="font-medium">💡 Tip:</span> We'll use your company name to suggest a custom login address in the next few steps.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <InputField
          id="companyName"
          name="companyName"
          label="Company name"
          value={formData.companyName}
          onChange={(value) => updateField('companyName', value)}
          error={errors.companyName}
          required
          placeholder="SA Mortgages"
          helpText={fcaValidation.companyData?.organisationName ? 
            `Auto-filled from FCA: ${fcaValidation.companyData.organisationName}` : 
            undefined
          }
        />

        <InputField
          id="companiesHouseNumber"
          name="companiesHouseNumber"
          label="Company number"
          value={formData.companiesHouseNumber}
          onChange={(value) => updateField('companiesHouseNumber', value)}
          error={errors.companiesHouseNumber}
          placeholder="473829923"
          helpText={fcaValidation.companyData?.companiesHouseNumber ?
            `Auto-filled from FCA: ${fcaValidation.companyData.companiesHouseNumber}` :
            undefined
          }
        />
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Previous
        </button>
        
        <button
          type="button"
          onClick={handleNext}
          disabled={!isStepValid()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
